import { useCallback, useContext, useEffect, useState } from "react";
import { getApiUrl } from "../utils/api";
import { Link, useNavigate } from "react-router-dom";
import {
  FaFire,
  FaQuestionCircle,
  FaUsers,
} from "react-icons/fa";
import BadgeIcon from "./BadgeIcon";
import SidebarFooter from "./SidebarFooter";
import { UserContext } from "../context/UserContext";
import { useToast } from "../context/ToastContext";
import {
  getAvatarUrl,
  DEFAULT_AVATAR_DATA_URL,
  FALLBACK_AVATAR_SVG,
} from "../utils/avatar";
import { usePaginatedTab } from "../hooks/usePaginatedTab";
import ProfileTabLoadMore from "./ProfileTabLoadMore";

function avatarOnError(e) {
  e.currentTarget.src =
    e.currentTarget.src === DEFAULT_AVATAR_DATA_URL
      ? FALLBACK_AVATAR_SVG
      : DEFAULT_AVATAR_DATA_URL;
}

function QuillIllustration() {
  return (
    <svg
      className="app-right-sidebar-quill mx-auto mb-3"
      viewBox="0 0 120 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M18 62c8-14 22-28 38-36 6-3 12-5 18-6-2 8-6 16-12 22-8 8-18 14-28 18-5 2-11 3-16 2z"
        stroke="#f26522"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M56 20c10-2 20 0 28 6 4 3 7 7 9 12"
        stroke="#f5a623"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <ellipse cx="88" cy="58" rx="14" ry="6" stroke="#f26522" strokeWidth="1.5" />
      <path d="M82 58h12" stroke="#f26522" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

const QUICK_LINKS = [
  { to: "/writers", label: "Writers", icon: FaUsers },
  { to: "/?sort=likes", label: "Top poems", icon: FaFire },
  { to: "/feedback", label: "Help center", icon: FaQuestionCircle },
];

const SIDEBAR_SCROLL_ROOTS = [".app-right-sidebar-scroll", ".home-page__feed-column"];

export default function HomeRightSidebar() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const toast = useToast();
  const [writers, setWriters] = useState([]);
  const [writersLoading, setWritersLoading] = useState(true);
  const [followingIds, setFollowingIds] = useState(() => new Set());
  const [followLoadingId, setFollowLoadingId] = useState(null);

  const fetchDiscoverGroupsPage = useCallback(
    async (page, limit) => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (user) params.set("discover", "true");

      const res = await fetch(getApiUrl(`/api/groups?${params}`), {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return { items: data.groups || [], pagination: data.pagination };
    },
    [user]
  );

  const groupsFeed = usePaginatedTab({
    enabled: true,
    resetKey: user?._id || "guest-groups",
    pageSize: 4,
    fetchPage: fetchDiscoverGroupsPage,
    scrollRootSelectors: SIDEBAR_SCROLL_ROOTS,
  });

  const groups = groupsFeed.items;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setWritersLoading(true);
      try {
        const res = await fetch(
          getApiUrl("/api/user/writers?page=1&limit=8"),
          { credentials: "include" }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const list = (data.users || []).filter(
          (w) => !user || String(w._id) !== String(user._id)
        );
        if (!cancelled) setWriters(list.slice(0, 5));
      } catch (err) {
        console.error("Failed to load writers:", err);
        if (!cancelled) setWriters([]);
      } finally {
        if (!cancelled) setWritersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?._id]);

  useEffect(() => {
    if (!user) {
      setFollowingIds(new Set());
      return;
    }

    (async () => {
      try {
        const res = await fetch(getApiUrl("/api/user/profile"), {
          credentials: "include",
        });
        if (!res.ok) return;
        const data = await res.json();
        const ids = new Set(
          (data.user?.following || []).map((u) => String(u._id || u))
        );
        setFollowingIds(ids);
      } catch (err) {
        console.error("Failed to load following list:", err);
      }
    })();
  }, [user?._id]);

  const handleShareWebsite = async () => {
    const url = window.location.origin;
    try {
      if (navigator.share) {
        await navigator.share({
          title: "PeerPoetry",
          text: "Join me on PeerPoetry — share poems and connect with writers.",
          url,
        });
        return;
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
    }

    try {
      await navigator.clipboard.writeText(url);
      toast.success("Website link copied to clipboard!");
    } catch {
      toast.error("Could not copy link. Please try again.");
    }
  };

  const handleFollow = async (writer) => {
    if (!user) {
      navigate("/login");
      return;
    }
    setFollowLoadingId(writer._id);
    try {
      const res = await fetch(
        getApiUrl(`/api/user/${writer.username}/follow`),
        { method: "PUT", credentials: "include" }
      );
      if (!res.ok) throw new Error("Follow failed");
      setFollowingIds((prev) => new Set(prev).add(String(writer._id)));
    } catch (err) {
      console.error("Follow error:", err);
      toast.error("Could not follow user. Please try again.");
    } finally {
      setFollowLoadingId(null);
    }
  };

  const handleUnfollow = async (writer) => {
    if (!user) return;
    setFollowLoadingId(writer._id);
    try {
      const res = await fetch(
        getApiUrl(`/api/user/${writer.username}/unfollow`),
        { method: "PUT", credentials: "include" }
      );
      if (!res.ok) throw new Error("Unfollow failed");
      setFollowingIds((prev) => {
        const next = new Set(prev);
        next.delete(String(writer._id));
        return next;
      });
    } catch (err) {
      console.error("Unfollow error:", err);
      toast.error("Could not unfollow user. Please try again.");
    } finally {
      setFollowLoadingId(null);
    }
  };

  return (
    <aside className="app-right-sidebar" aria-label="Home sidebar">
      <div className="app-right-sidebar-scroll">
      <div className="app-right-sidebar-panel">
        <section className="app-right-sidebar-card">
          <div className="home-rail-card-head">
            <h2 className="app-right-sidebar-card__title app-right-sidebar-card__title--dark mb-0">
              Poets to follow
            </h2>
            <Link to="/writers" className="home-rail-card-head__view-all">
              View all
            </Link>
          </div>
          {writersLoading ? (
            <p className="app-right-sidebar-hint">Loading writers...</p>
          ) : writers.length === 0 ? (
            <p className="app-right-sidebar-hint">
              No writers to suggest yet.{" "}
              <Link to="/writers" className="app-right-sidebar-inline-link">
                Browse all
              </Link>
            </p>
          ) : (
            <ul className="app-right-sidebar-list">
              {writers.map((writer) => {
                const isFollowing = followingIds.has(String(writer._id));
                return (
                  <li key={writer._id} className="app-right-sidebar-item">
                    <Link
                      to={`/users/${writer.username}`}
                      className="app-right-sidebar-user"
                      title={writer.username}
                    >
                      <img
                        src={getAvatarUrl(writer.avatar)}
                        alt=""
                        className="app-right-sidebar-avatar"
                        onError={avatarOnError}
                      />
                      <span className="app-right-sidebar-name">
                        {writer.username}
                        <BadgeIcon type={writer.badge} className="text-[#f26522]" />
                      </span>
                    </Link>
                    {user && !isFollowing && (
                      <button
                        type="button"
                        className="app-right-sidebar-invite"
                        onClick={() => handleFollow(writer)}
                        disabled={followLoadingId === writer._id}
                      >
                        {followLoadingId === writer._id ? "..." : "Follow"}
                      </button>
                    )}
                    {user && isFollowing && (
                      <button
                        type="button"
                        className="app-right-sidebar-status"
                        onClick={() => handleUnfollow(writer)}
                        disabled={followLoadingId === writer._id}
                        title="Unfollow"
                      >
                        {followLoadingId === writer._id ? "..." : "Unfollow"}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="app-right-sidebar-card">
          <h2 className="app-right-sidebar-card__title">Discover groups</h2>
          {groupsFeed.loading ? (
            <p className="app-right-sidebar-hint">Loading groups...</p>
          ) : groups.length === 0 ? (
            <p className="app-right-sidebar-hint">
              {user
                ? "You're in all available groups, or none exist yet."
                : "Log in to join poetry groups."}
            </p>
          ) : (
            <ul className="app-right-sidebar-groups">
              {groups.map((group) => (
                <li key={group._id}>
                  <Link
                    to={`/groups/${group._id}`}
                    className="app-right-sidebar-groups__link"
                  >
                    <FaUsers
                      className="app-right-sidebar-groups__icon"
                      aria-hidden="true"
                    />
                    <span className="app-right-sidebar-groups__info">
                      <span className="app-right-sidebar-groups__name">
                        {group.name}
                      </span>
                      <span className="app-right-sidebar-groups__meta">
                        {group.memberCount ?? 0} member
                        {group.memberCount === 1 ? "" : "s"}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <ProfileTabLoadMore
            compact
            sentinelRef={groupsFeed.sentinelRef}
            loadingMore={groupsFeed.loadingMore}
            hasNextPage={groupsFeed.pagination.hasNextPage}
            onLoadMore={groupsFeed.loadMore}
            itemCount={groups.length}
          />
        </section>

        <section className="app-right-sidebar-card">
          <h2 className="app-right-sidebar-card__title">Quick links</h2>
          <ul className="app-right-sidebar-quick-links">
            {QUICK_LINKS.map(({ to, label, icon: Icon }) => (
              <li key={to + label}>
                <Link to={to} className="app-right-sidebar-quick-links__link">
                  <Icon className="app-right-sidebar-quick-links__icon" aria-hidden="true" />
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section className="app-right-sidebar-card app-right-sidebar-card--invite">
          <div className="home-invite-widget">
            <QuillIllustration />
            <p className="home-invite-widget__text">
              Love this community? Share PeerPoetry with fellow poets and grow our circle.
            </p>
            <button
              type="button"
              className="home-invite-widget__btn"
              onClick={handleShareWebsite}
            >
              Share platform
            </button>
          </div>
        </section>
      </div>
      <SidebarFooter />
      </div>
    </aside>
  );
}
